"use client";

import { useEffect, useState } from "react";
import { globeFrameSchema } from "@/schemas/gdelt";
import type { GlobeFrame, GlobeStreamSource, HotZone } from "@/types/gdelt";

type StreamState = {
  frame: GlobeFrame | null;
  status: "connecting" | "open" | "closed" | "error";
  error: string | null;
};

type StreamOptions = {
  enabled?: boolean;
  pollIntervalMs?: number;
};

export function useGlobeFrameStream(source: GlobeStreamSource, initialFrame?: GlobeFrame, options: StreamOptions = {}) {
  const enabled = options.enabled ?? true;
  const [state, setState] = useState<StreamState>({
    frame: initialFrame ?? null,
    status: initialFrame ? "open" : "connecting",
    error: null,
  });

  useEffect(() => {
    if (!enabled) {
      setState((current) => ({
        frame: initialFrame ?? current.frame,
        status: initialFrame || current.frame ? "open" : "closed",
        error: null,
      }));
      return;
    }

    let disposed = false;
    let close: (() => void) | undefined;

    setState((current) => ({
      ...current,
      status: current.frame ? "open" : "connecting",
      error: null,
    }));

    if (source.mode === "websocket") {
      const socket = new WebSocket(source.url);
      close = () => socket.close();

      socket.addEventListener("open", () => {
        if (!disposed) setState((current) => ({ ...current, status: "open", error: null }));
      });
      socket.addEventListener("message", (event) => {
        const frame = parseFrame(event.data);
        if (!disposed && frame) setState({ frame, status: "open", error: null });
      });
      socket.addEventListener("error", () => {
        if (!disposed) setState((current) => ({ ...current, status: "error", error: "WebSocket stream error" }));
      });
      socket.addEventListener("close", () => {
        if (!disposed) setState((current) => ({ ...current, status: "closed" }));
      });
    } else if (source.mode === "eventsource") {
      const eventSource = new EventSource(source.url);
      close = () => eventSource.close();

      eventSource.addEventListener("open", () => {
        if (!disposed) setState((current) => ({ ...current, status: "open", error: null }));
      });
      eventSource.addEventListener("message", (event) => {
        const frame = parseFrame(event.data);
        if (!disposed && frame) setState({ frame, status: "open", error: null });
      });
      eventSource.addEventListener("error", () => {
        if (!disposed) setState((current) => ({ ...current, status: "error", error: "EventSource stream error" }));
      });
    } else if (source.mode === "polling") {
      const poll = async () => {
        try {
          const response = await fetch(source.url, { cache: "no-store" });
          if (!response.ok) throw new Error(`Frame request failed: ${response.status}`);
          const frame = parseFrame(await response.json());
          if (!frame) throw new Error("Frame response was invalid");
          if (!disposed) setState({ frame, status: "open", error: null });
        } catch (error) {
          if (!disposed) setState((current) => ({ ...current, status: "error", error: (error as Error).message }));
        }
      };
      void poll();
      const interval = setInterval(poll, options.pollIntervalMs ?? 60_000);
      close = () => clearInterval(interval);
    } else {
      let timeout: ReturnType<typeof setTimeout> | undefined;
      close = () => {
        if (timeout) clearTimeout(timeout);
      };

      fetch(source.url)
        .then((response) => {
          if (!response.ok) throw new Error(`Mock frame request failed: ${response.status}`);
          return response.json() as Promise<unknown>;
        })
        .then((value) => {
          const frame = parseFrame(value);
          if (!frame) throw new Error("Mock frame response was invalid");
          if (disposed) return;
          setState({ frame, status: "open", error: null });

          let tick = 1;
          const emit = () => {
            if (disposed) return;
            setState({ frame: evolveMockFrame(frame, tick), status: "open", error: null });
            tick += 1;
            timeout = setTimeout(emit, 2800);
          };

          timeout = setTimeout(emit, 2800);
        })
        .catch((error: Error) => {
          if (!disposed) setState((current) => ({ ...current, status: "error", error: error.message }));
        });
    }

    return () => {
      disposed = true;
      close?.();
    };
  }, [enabled, initialFrame, options.pollIntervalMs, source.mode, source.url]);

  return state;
}

function parseFrame(data: unknown): GlobeFrame | null {
  try {
    const value = typeof data === "string" ? JSON.parse(data) as unknown : data;
    const result = globeFrameSchema.safeParse(value);
    return result.success ? result.data : null;
  } catch {
    return null;
  }
}

function evolveMockFrame(frame: GlobeFrame, tick: number): GlobeFrame {
  const zones = frame.zones.map((zone, index) => evolveMockZone(zone, tick, index));
  const category_breakdown = zones.reduce<GlobeFrame["category_breakdown"]>(
    (breakdown, zone) => {
      breakdown[zone.category] = (breakdown[zone.category] ?? 0) + zone.event_count;
      return breakdown;
    },
    {
      social_unrest: 0,
      crime: 0,
      corruption: 0,
      political_violence: 0,
      institutional_instability: 0,
    },
  );

  return {
    ...frame,
    generated_at: new Date(Date.now() + tick * 1000).toISOString(),
    global_intensity: Math.round(zones.reduce((total, zone) => total + zone.intensity, 0) / Math.max(zones.length, 1)),
    category_breakdown,
    zones,
  };
}

function evolveMockZone(zone: HotZone, tick: number, index: number): HotZone {
  const wave = Math.sin((tick + index) * 0.7);
  const trendDelta = zone.trend === "rising" ? 4 : zone.trend === "falling" ? -3 : 1;
  const intensity = clamp(Math.round(zone.intensity + wave * 5 + trendDelta), 12, 99);

  return {
    ...zone,
    intensity,
    event_count: Math.max(1, Math.round(zone.event_count + wave * 3 + trendDelta)),
    mention_count: Math.max(1, Math.round(zone.mention_count + wave * 24 + trendDelta * 9)),
    updated_at: new Date(Date.now() + tick * 1000).toISOString(),
  };
}

function clamp(value: number, min: number, max: number) {
  return Math.min(Math.max(value, min), max);
}
